import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ContentIdea } from '@/types/dashboard';


// Simple in-memory cache to prevent refetching on navigation
const trashCache: { userId: string | null; data: ContentIdea[]; timestamp: number } | null = { userId: null, data: [], timestamp: 0 };
const CACHE_DURATION = 60000; // 1 minute cache

export const useTrashContent = (userId: string | null) => {
  const [isLoading, setIsLoading] = useState(() => {
    if (trashCache && trashCache.userId === userId && trashCache.data.length > 0) {
      return false;
    }
    return false;
  });
  const [trashIdeas, setTrashIdeas] = useState<ContentIdea[]>(() => {
    if (trashCache && trashCache.userId === userId) {
      return trashCache.data;
    }
    return [];
  });
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchTrashContent = useCallback(async (isBackgroundRefresh = false) => {
    if (!userId) return;
    
    // Only show loading on initial fetch, not on background refreshes
    if (!isBackgroundRefresh) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Fetch trashed content ideas
      const { data: scrapedData, error: scrapedError } = await supabase
        .from('content_ideas')
        .select(`
          *,
          source_competitor:scraped_content!content_ideas_source_scraped_content_id_fkey (
            username,
            likes_count,
            comments_count,
            engagement_rate,
            url,
            video_url
          )
        `)
        .eq('user_id', userId)
        .eq('is_trashed', true)
        .order('trashed_at', { ascending: false });

      if (scrapedError) throw scrapedError;

      // Fetch trashed user-generated content
      const { data: generatedData, error: generatedError } = await supabase
        .from('user_generated_content')
        .select('*')
        .eq('user_id', userId)
        .eq('is_trashed', true)
        .order('trashed_at', { ascending: false });

      if (generatedError) throw generatedError;

      // Map scraped content with explicit user_id filter (defense-in-depth)
      const mappedScraped: ContentIdea[] = (scrapedData || [])
        .filter(idea => idea.user_id === userId) // Explicit security check
        .map(idea => ({
        id: idea.id,
        user_id: idea.user_id,
        source_scraped_content_id: idea.source_scraped_content_id,
        title: idea.title,
        description: idea.description,
        content: idea.content,
        edited_content: idea.edited_content,
        platform: idea.platform,
        viral_score: idea.viral_score,
        hashtags: idea.hashtags,
        created_at: idea.created_at,
        updated_at: idea.updated_at,
        is_favorited: idea.is_favorited,
        favorited_at: idea.favorited_at,
        is_used: idea.is_used,
        used_at: idea.used_at,
        is_trashed: idea.is_trashed,
        trashed_at: idea.trashed_at,
        alt_hooks: idea.alt_hooks,
        text_overlays: idea.text_overlays,
        improvements_applied: idea.improvements_applied,
        perplexity_insights: idea.perplexity_insights,
        source: 'scraped' as const,
        source_competitor: idea.source_competitor ? {
          username: idea.source_competitor.username,
          likes_count: idea.source_competitor.likes_count || 0,
          comments_count: idea.source_competitor.comments_count || 0,
          engagement_rate: idea.source_competitor.engagement_rate || 0,
          url: idea.source_competitor.url || '',
          video_url: idea.source_competitor.video_url,
        } : undefined
      }));

      // Map generated content with explicit user_id filter (defense-in-depth)
      const mappedGenerated: ContentIdea[] = (generatedData || [])
        .filter(idea => idea.user_id === userId) // Explicit security check
        .map(idea => ({
        id: idea.id,
        user_id: idea.user_id,
        source_scraped_content_id: null,
        title: idea.title,
        description: idea.description,
        content: idea.content,
        edited_content: idea.edited_content,
        platform: idea.platform || 'instagram',
        viral_score: null,
        hashtags: null,
        created_at: idea.created_at,
        updated_at: idea.updated_at,
        is_favorited: idea.is_favorited,
        favorited_at: idea.favorited_at,
        is_used: idea.is_used,
        used_at: idea.used_at,
        is_trashed: idea.is_trashed,
        trashed_at: idea.trashed_at,
        alt_hooks: idea.alt_hooks,
        text_overlays: idea.text_overlays,
        improvements_applied: idea.improvements_applied,
        perplexity_insights: idea.perplexity_insights,
        source: 'user_generated' as const,
        main_idea: idea.main_idea,
        source_competitor: undefined
      }));

      // Combine and sort by trashed_at
      const allTrash = [...mappedScraped, ...mappedGenerated].sort((a, b) => {
        const dateA = new Date(a.trashed_at || 0).getTime();
        const dateB = new Date(b.trashed_at || 0).getTime();
        return dateB - dateA;
      });

      setTrashIdeas(allTrash);
      setLastRefresh(new Date());
      
      // Update cache
      trashCache.userId = userId;
      trashCache.data = allTrash;
      trashCache.timestamp = Date.now();
    } catch (err) {
      // Ignore transient network errors during navigation (Load failed)
      const errorMessage = err instanceof Error ? err.message : 'Failed to load trash';
      const isTransientError = errorMessage.includes('Load failed') || errorMessage.includes('aborted');
      
      if (!isTransientError) {
        console.error('Error fetching trash content:', err);
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const refresh = useCallback(() => {
    fetchTrashContent(true); // Pass true for background refresh
  }, [fetchTrashContent]);

  // Initial fetch - skip if cache is fresh
  useEffect(() => {
    if (!userId) return;
    
    const isCacheFresh = trashCache.userId === userId && 
      trashCache.data.length > 0 && 
      (Date.now() - trashCache.timestamp) < CACHE_DURATION;
    
    if (!isCacheFresh) {
      fetchTrashContent();
    }
  }, [userId, fetchTrashContent]);

  return {
    isLoading,
    trashIdeas,
    error,
    lastRefresh,
    refresh
  };
};
